import React, { createContext, useEffect, useState } from 'react';
import { useActivesWallet } from '../hooks/ActivesHooks';
import { IPropsActiveWallet, IPropsGeneralActiveWallet } from '../@types';
import { handleToast } from '../../../../components/Toast';
import axios from 'axios';
import { financeRoutes, walletRoutes } from '../../../../utils/RoutesApi';
import { IPropsException } from '../../../components/RenderConditionalObjective';
import { transformMaskCurrency } from '../../../../utils/transformMaskCurrency';
import { baseURL } from '../../../../utils/baseURL';
import {
  BoxPctMap,
  EMPTY_BOX_PCT_MAP,
  applyBoxAllocation,
  buildBoxPctMap,
  isVisibleAfterBox,
} from '../Tabs/AllActive/boxAllocation';

interface IPropsSimulationProvider {
  children: React.ReactNode;
}

interface IPropsSimulationContextValue {
  actions: IPropsActiveWallet[];
  fiis: IPropsActiveWallet[];
  all: IPropsGeneralActiveWallet[];
  selected: IPropsActiveWallet[];
  selectedFiis: IPropsActiveWallet[];
  selectedAll: IPropsActive[];
  valueAporte: number | string;
  sendAllAssets: 'all' | 'selected';
  totalAport: string;
  rowSelectionAction: any;
  rowSelectionFii: any;
  rowSelectionAll: any;
  setSendAllAssets: (sendAllAssets: 'all' | 'selected') => void;
  seValueAporte: (val: number) => void;
  setType: (
    type:
      | 'action'
      | 'fii'
      | 'all'
      | 'broker'
      | 'stocks'
      | 'etf'
      | 'reit'
      | 'crypto'
  ) => void;
  submitSimulator: (data: any) => void;
  setResults: Function;
  type:
    | 'action'
    | 'fii'
    | 'all'
    | 'broker'
    | 'stocks'
    | 'etf'
    | 'reit'
    | 'crypto';
  selectedTypes: string[];
  setSelectedTypes: (types: string[]) => void;
  loading: boolean;
  results: IPropsActive[];
  updateValueTotal: number;
  typeWallet: boolean;
}

export const SimulationContext = createContext<IPropsSimulationContextValue>({
  actions: [],
  fiis: [],
  all: [],
  selected: [],
  selectedFiis: [],
  selectedAll: [],
  valueAporte: 0,
  sendAllAssets: 'all',
  totalAport: '',
  rowSelectionAction: {},
  rowSelectionFii: {},
  rowSelectionAll: {},
  seValueAporte: () => {},
  setType: () => {},
  setSendAllAssets: () => {},
  submitSimulator: () => {},
  type: 'all',
  selectedTypes: ['all'],
  setSelectedTypes: () => {},
  results: [],
  loading: false,
  updateValueTotal: 0,
  setResults: () => {},
  typeWallet: false,
});

interface IPropsActive {
  Exception: IPropsException[];
  ValorExp: number;
  corporationName: string;
  novaQuantidade: number;
  aportarQtde: number;
  closingPrice: number;
  equitiesQuantity: number;
  novoupdateValue: number;
  percent: number;
  tickerSymbol: string;
  updateValue: number;
  key: number;
  target: number;
  newpercent: number;
  totalPercent: number;
  moneyAporte: number;
  finalPercent: number;
  productTypeName: string;
  porcentagem_recomendada: number;
}


const RULE_TYPE_BY_PRODUCT: Record<string, string> = {
  acao: 'action',
  fii: 'fii',
  stock: 'stock',
  stocks: 'stock',
  reit: 'reit',
  reits: 'reit',
  etf: 'etf',
  etfs: 'etf',
  crypto: 'crypto',
  cripto: 'crypto',
};

function getSimulationObjective(item: any, fallbackObjective?: number) {
  const productRuleType =
    RULE_TYPE_BY_PRODUCT[item.productTypeName] ?? 'all';

  const exceptions = item.Exception ?? [];

  // Primeiro procura a regra específica do tipo.
  // Ex.: CSUD3 -> action
  const specificRule = exceptions.find(
    (exception: any) => exception.type === productRuleType
  );

  if (specificRule) {
    return Number(specificRule.value ?? 0);
  }

  // Compatibilidade com regras antigas type="all".
  const allRule = exceptions.find(
    (exception: any) => exception.type === 'all'
  );

  if (allRule) {
    return Number(allRule.value ?? 0);
  }

  // Sem regra: prioriza o peso calculado pela carteira recomendada. Quando
  // ele não existe/é 0 (ex.: typeWallet desligado, ou tipo sem peso
  // calculado ainda), cai no objetivo fixo do tipo (objetivoFII, objetivoAcao,
  // objetivoAllX...) — a mesma fonte usada antes dessa função existir, pra
  // não sumir com o ativo só por falta de peso calculado.
  if (item.porcentagem_recomendada) {
    return Number(item.porcentagem_recomendada);
  }

  if (fallbackObjective != null) {
    return Number(fallbackObjective);
  }

  return Number(item.ValorExp ?? 0);
}

// Em carteira recomendada (typeWallet), porcentagem_recomendada 0 sem regra
// (Exception) explícita costuma indicar que o motor de recomendação ainda não
// calcula peso pra esse tipo (ex.: ETF) — não é meta deliberada como pode ser
// pra uma ação/FII específico excluído da carteira. Não esconder o ativo da
// seleção nesse caso; o ValorExp segue 0 (não inventamos objetivo) pra não
// distorcer o cálculo de aporte feito no backend.
function shouldListInAporte(item: any, objective: number, typeWallet: boolean) {
  if (Number(objective) > 0) return true;

  const hasExplicitRule = (item.Exception ?? []).length > 0;

  return typeWallet && !hasExplicitRule;
}

// Fallback do bucket "all" (etf/stock/reit/crypto/multi-seleção) por tipo de
// produto — mesma cadeia usada antes da função getSimulationObjective existir.
function getAllBucketFallback(item: any, wallet: any) {
  if (item.productTypeName === 'acao') {
    return wallet?.objetivoAllAcao ?? wallet?.objetivoAll;
  }
  if (item.productTypeName === 'fii') {
    return wallet?.objetivoAllFII ?? wallet?.objetivoAll;
  }
  return wallet?.objetivoAllRenda ?? wallet?.objetivoAll;
}

export const SimulationProvider = ({ children }: IPropsSimulationProvider) => {
  const { fullWallet, typeWallet } = useActivesWallet();
  const wallet = fullWallet;

  const [actions, setActions] = useState<IPropsActiveWallet[]>([]);
  const [fiis, setFiis] = useState<IPropsActiveWallet[]>([]);
  const [all, setAll] = useState<IPropsGeneralActiveWallet[]>([]);
  const [selected, setSelected] = useState<IPropsActiveWallet[]>([]);
  const [selectedFiis, setSelectedFiis] = useState<IPropsActiveWallet[]>([]);
  const [selectedAll, setSelectedAll] = useState<IPropsActive[]>([]);
  const [results, setResults] = useState<IPropsActive[]>([]);
  const [updateValueTotal, setUpdateValue] = useState(0);
  const [valueAporte, seValueAporte] = useState<number | string>(0);
  const [sendAllAssets, setSendAllAssets] = useState<'all' | 'selected'>(
    'selected'
  );
  const [type, setType] = useState<
    | 'action'
    | 'fii'
    | 'all'
    | 'broker'
    | 'stocks'
    | 'etf'
    | 'reit'
    | 'crypto'
  >('all');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['all']);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalAport, setTotalAport] = useState<string>('');
  const [boxPct, setBoxPct] = useState<BoxPctMap>(EMPTY_BOX_PCT_MAP);

  useEffect(() => {
    setResults([]);
  }, [sendAllAssets, type]);

  // Busca os boxes (objetivos/reservas) para descontar da `carteira` enviada à
  // simulação a renda fixa já alocada — mesma regra do AllActive: 100% alocado
  // sai; parcial entra só com a diferença livre. Ver boxAllocation.ts.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${baseURL}${financeRoutes.FINANCE_BOX_LIST}`);
        if (active) setBoxPct(buildBoxPctMap(res.data || []));
      } catch (error) {
        // Sem boxes ou falha de rede: carteira segue sem ajuste.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (wallet && wallet?.positionsAll?.length > 0) {
      if (sendAllAssets === 'all') {
        if (type === 'all') {
          const newItem = wallet?.positionsAll
            ?.filter((item) => !item?.alocado_total)
            ?.map((item) => {
              const returnItem = { ...item };

              returnItem.ValorExp = getSimulationObjective(
                item,
                getAllBucketFallback(item, wallet)
              );

              returnItem.key =
                Math.floor(Math.random() * (99999 - 1)) + 1;

              return returnItem;
            })
            .filter((item) => shouldListInAporte(item, item.ValorExp, typeWallet));

          setAll(newItem);
        }

        if (type === 'broker') {
          let newItem = wallet?.positionsAll?.filter(item => !item?.alocado_total)?.map((item) => {
            let returnItem = { ...item };

            let isRuleReadyExist = returnItem.Exception.find(
              (ex) => ex.type === 'all'
            );
            if (isRuleReadyExist) {
              returnItem.ValorExp = isRuleReadyExist?.value;
            } else {
              if (returnItem.productTypeName === 'acao') {
                returnItem.ValorExp = wallet?.objetivoAllAcao || wallet?.objetivoAll || returnItem.ValorExp;
              } else if (returnItem.productTypeName === 'fii') {
                returnItem.ValorExp = wallet?.objetivoAllFII || wallet?.objetivoAll || returnItem.ValorExp;
              } else {
                returnItem.ValorExp = wallet?.objetivoAllRenda || wallet?.objetivoAll || returnItem.ValorExp;
              }
            }
            if (typeWallet) {
              returnItem.ValorExp = item.porcentagem_recomendada || returnItem.ValorExp;
            }
            returnItem.key = Math.floor(Math.random() * (99999 - 1)) + 1;

            // if (returnItem.tickerSymbol === 'CSUD3') {
            //   console.log('===== DEBUG SIMULATION PROVIDER =====');
            //   console.log('ticker:', returnItem.tickerSymbol);
            //   console.log('productTypeName:', returnItem.productTypeName);
            //   console.log('Exception:', returnItem.Exception);
            //   console.log('objetivoAllAcao:', wallet?.objetivoAllAcao);
            //   console.log(
            //     'porcentagem_recomendada:',
            //     item.porcentagem_recomendada
            //   );
            //   console.log('ValorExp FINAL:', returnItem.ValorExp);
            //   console.log('=====================================');
            // }

            return returnItem;
          });
          setAll(newItem);
        }

        if (type === 'fii') {
          const newItem = wallet?.positionFII
            ?.map((item) => {
              const returnItem = { ...item };

              returnItem.ValorExp = getSimulationObjective(
                item,
                wallet?.objetivoFII
              );

              returnItem.key =
                Math.floor(Math.random() * (99999 - 1)) + 1;

              return returnItem;
            })
            .filter((item) => Number(item.ValorExp) > 0);

          setFiis(newItem);
        }

        if (type === 'action') {
          const newItem = wallet?.positionAcao
            ?.map((item) => {
              const returnItem = { ...item };

              returnItem.ValorExp = getSimulationObjective(
                item,
                wallet?.objetivoAcao
              );

              returnItem.key =
                Math.floor(Math.random() * (99999 - 1)) + 1;

              return returnItem;
            })
            .filter((item) => Number(item.ValorExp) > 0);

          setActions(newItem);
        }

      } else {
        if (type === 'all') {
          const newItem = wallet?.positionsAll
            ?.filter((item) => !item?.alocado_total)
            ?.map((item) => {
              const returnItem = { ...item };

              returnItem.ValorExp = getSimulationObjective(
                item,
                getAllBucketFallback(item, wallet)
              );

              returnItem.key =
                Math.floor(Math.random() * (99999 - 1)) + 1;

              return returnItem;
            })
            .filter((item) => shouldListInAporte(item, item.ValorExp, typeWallet))
            // "Ativos abaixo do objetivo": só entra quem ainda não bateu a meta.
            .filter((item) => item.percent < item.ValorExp);

          setAll(newItem);
        }
        if (type === 'action') {
          const newItem = wallet?.positionAcao
            ?.map((item) => {
              const returnItem = { ...item };

              returnItem.ValorExp = getSimulationObjective(
                item,
                wallet?.objetivoAcao
              );

              returnItem.key =
                Math.floor(Math.random() * (99999 - 1)) + 1;

              return returnItem;
            })
            .filter((item) => Number(item.ValorExp) > 0)
            .filter((item) => item.percent < item.ValorExp);

          setActions(newItem);
        }
        if (type === 'fii') {
          const newItem = wallet?.positionFII
            ?.map((item) => {
              const returnItem = { ...item };

              returnItem.ValorExp = getSimulationObjective(
                item,
                wallet?.objetivoFII
              );

              returnItem.key =
                Math.floor(Math.random() * (99999 - 1)) + 1;

              return returnItem;
            })
            .filter((item) => Number(item.ValorExp) > 0)
            .filter((item) => item.percent < item.ValorExp);

          setFiis(newItem);
        }
        if (type === 'broker') {
          let newItem = wallet?.positionsAll.map((item) => {
            let returnItem = { ...item };

            let isRuleReadyExist = returnItem.Exception.find(
              (ex) => ex.type === 'all'
            );
            if (isRuleReadyExist) {
              returnItem.ValorExp = isRuleReadyExist.value;
            } else {
              returnItem.ValorExp = wallet?.objetivoAll || returnItem.ValorExp;
            }
            returnItem.key = Math.floor(Math.random() * (99999 - 1)) + 1;
            if (typeWallet) {
              returnItem.ValorExp = item.porcentagem_recomendada || returnItem.ValorExp;
            }
            return returnItem;
          });
          newItem = newItem.filter((item) => item.percent < item.ValorExp);

          setAll(newItem);
        }
      }
    }
  }, [wallet, sendAllAssets, type, typeWallet]);

  useEffect(() => {
    rowSelectionAction.onChange(
      actions?.map((item) => item.key),
      actions
    );
    rowSelectionFii.onChange(
      fiis?.map((item) => item.key),
      fiis
    );
    rowSelectionAll.onChange(
      all?.map((item) => item.key),
      all
    );
  }, [fiis, actions, all, sendAllAssets, type]);

  const rowSelectionAction = {
    //@ts-ignore

    onChange: (selectedRowKeys, selectedRows) => {
      setSelected(selectedRows);
    },
    selectedRowKeys: selected?.map((item) => item.key),
  };
  const rowSelectionFii = {
    //@ts-ignore
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedFiis(selectedRows);
    },
    selectedRowKeys: selectedFiis?.map((item) => item.key),
  };
  const rowSelectionAll = {
    //@ts-ignore
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedAll(selectedRows);
    },
    selectedRowKeys: selectedAll?.map((item) => item.key),
  };

  async function submitSimulator(data: any) {
    setLoading(true);

    if (valueAporte) {
      data.aporte = valueAporte
        .toString()
        .replace(/\D/g, '')
        .replace(/(\d)(\d{2})$/, '$1.$2');
    }
    if (!data.aporte) {
      setLoading(false);
      return handleToast(true, 'Informe o valor do aporte');
    }
    // sempre envia array — multi-seleção do filtro
    data.type = selectedTypes.length > 0 ? selectedTypes : ['all'];

    // Um único tipo selecionado (exceto 'all') vai para o endpoint dedicado
    // `simulacao_aporte` (via `tipoativo`); 'all'/múltiplos tipos vão para
    // `simulacao_aporte_todos`.
    const isSingleType =
      selectedTypes.length === 1 && selectedTypes[0] !== 'all';
    if (isSingleType) {
      data.tipoativo = selectedTypes[0];
    }
    const submitRoute = isSingleType
      ? walletRoutes.WALLET_SIMULATOR_SUBMISSION
      : walletRoutes.WALLET_SIMULATOR_ALL_SUBMISSION;

    // Novo contrato do backend: o simulador opera 100% sobre o payload. Além de
    // `ativos` (subconjunto selecionado), enviamos `carteira` com TODAS as
    // posições (fonte autoritativa) para o cálculo correto de gap por tipo e
    // newpercent relativo ao patrimônio total. Os valores de positionsAll já
    // vêm em BRL (intl convertido), então não enviamos exchange_rate_brl.
    //
    // Renda fixa alocada em box NÃO entra na carteira: aplicamos a mesma regra
    // do AllActive (applyBoxAllocation) — 100% alocado sai; parcial entra apenas
    // com a diferença livre (updateValue/equitiesQuantity reduzidos).
    data.carteira = (wallet?.positionsAll || [])
      .map((item) => applyBoxAllocation(item, boxPct))
      .filter(isVisibleAfterBox)
      .map((item) => ({
        tickerSymbol: item.tickerSymbol,
        productTypeName: item.productTypeName,
        equitiesQuantity: item.equitiesQuantity,
        updateValue: item.updateValue,
        closingPrice: item.closingPrice,
        reservadeemergencia: item.reservadeemergencia,
      }));

    if (type === 'action') {
      if (selected.length <= 0) {
        setLoading(false);

        return handleToast(true, 'Selecione pelo menos 1 ativo');
      }

      data.ativos = selected;
    } else if (type === 'fii') {
      if (selectedFiis.length <= 0) {
        setLoading(false);

        return handleToast(true, 'Selecione pelo menos 1 ativo');
      }
      data.ativos = selectedFiis;
    }
    if (type === 'all' || type === 'broker') {
      if (selectedAll.length <= 0) {
        setLoading(false);
        return handleToast(true, 'Selecione pelo menos 1 ativo');
      }
      data.ativos = selectedAll;
      await axios
        .post(
          `${process.env.REACT_APP_API_URL}${submitRoute}`,
          data
        )
        .then((response) => {
          let totalUpdateValue = 0;
          response.data.data.map((item: IPropsActive) => {
            totalUpdateValue = item.updateValue + totalUpdateValue;
          });
          setUpdateValue(totalUpdateValue);

          const newData = response.data.data.map((item: IPropsActive) => {
            return {
              ...item,
              moneyAporte: item.aportarQtde * item.closingPrice,
            };
          });

          const reduceTotalAport = newData.reduce(
            (acc: number, item: IPropsActive) => {
              return acc + item.moneyAporte;
            },
            0
          );
          const maskTotalAport = transformMaskCurrency(reduceTotalAport);
          setTotalAport(maskTotalAport);
          setLoading(false);
          setResults(newData);
          handleToast(false, 'Simulação concluída com sucesso');

          return;
        })
        .catch((err) => {
          handleToast(true, err.response.data.message);
          setLoading(false);

          return;
        });
      return;
    }

    await axios
      .post(
        `${process.env.REACT_APP_API_URL}${submitRoute}`,
        data
      )
      .then((response) => {
        let totalUpdateValue = 0;
        response.data.data.map((item: IPropsActive) => {
          totalUpdateValue = item.updateValue + totalUpdateValue;
        });

        const reduceTotalAport = response.data.data
          .map((item: IPropsActive) => {
            return {
              ...item,
              moneyAporte: item.aportarQtde * item.closingPrice,
            };
          })
          .reduce((acc: number, item: IPropsActive) => {
            return acc + item.moneyAporte;
          }, 0);

        const maskTotalAport = transformMaskCurrency(reduceTotalAport);
        setTotalAport(maskTotalAport);
        setUpdateValue(totalUpdateValue);
        setResults(
          response.data.data.map((item: IPropsActive) => {
            return {
              ...item,
              moneyAporte: item.aportarQtde * item.closingPrice,
            };
          })
        );
        handleToast(false, 'Simulação concluída com sucesso');
        setLoading(false);

        return;
      })
      .catch((err) => {
        handleToast(true, err.response.message);

        setLoading(false);

        return;
      });
  }

  return (
    <SimulationContext.Provider
      value={{
        actions,
        all,
        fiis,
        rowSelectionAction,
        rowSelectionAll,
        rowSelectionFii,
        selected,
        selectedAll,
        selectedFiis,
        sendAllAssets,
        totalAport,
        valueAporte,
        setType,
        seValueAporte,
        setSendAllAssets,
        type,
        selectedTypes,
        setSelectedTypes,
        results,
        updateValueTotal,
        submitSimulator,
        loading,
        setResults,
        typeWallet,
      }}
    >
      {children}
    </SimulationContext.Provider>
  );
};
